import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import {
  Button,
  Dialog,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogContentText,
  DialogTitle,
  TextField as MuiTextField,
  Typography,
  Autocomplete,
} from "@mui/material";
import { RootState } from "../../../redux/store";
import {
  setBookmarkDialogState,
  setPreUploadedBookmarks,
} from "../../../redux/slices/bookmark";
import { addBookmarkToDB } from "../../../firebase/utils";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import { SnackbarFns } from "../../../utils/fnsRedux";

const TextField = styled(MuiTextField)`
  margin-top: 25px;
`;

const DialogActions = styled(MuiDialogActions)<{ error: boolean }>`
  display: flex;
  justify-content: ${(props) => (props.error ? "space-between" : "flex-end")};
  padding-right: 18px;
`;

const DialogContent = styled(MuiDialogContent)`
  padding-bottom: 0px;
`;

const BookmarkDialog = () => {
  const dispatch = useAppDispatch();
  const handleSnackbar = SnackbarFns();
  const bookmarkState = useAppSelector((state: RootState) => state.bookmark);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [group, setGroup] = useState("");
  const [error, setError] = useState(false);
  const groups = bookmarkState.bookmarks.map((bookmark) => bookmark.group);
  const titles = bookmarkState.bookmarks.map((bookmark) => bookmark.title);
  const uniqueGroups = Array.from(new Set(groups).values());
  const uniqueTitles = Array.from(new Set(titles));

  const [bookmarkIdx, setBookmarkIdx] = useState<number>(
    bookmarkState.bookmarks.length
  );

  useEffect(() => {
    setBookmarkIdx(bookmarkState.bookmarks.length + 1);
  }, [bookmarkState.bookmarks]);

  useEffect(() => {
    setTitle(bookmarkState.preUploadedBookmarks[0]?.title);
  }, [bookmarkState.bookmarkDialogOpen]);

  const handleSubmit = () => {
    if (title !== "") {
      bookmarkState.preUploadedBookmarks.forEach(async (bookmark, idx) => {
        const currBookmark = { ...bookmark };
        currBookmark.title = title;
        currBookmark.description = description;
        currBookmark.group = group === "" ? "Others" : group;
        currBookmark.index = bookmarkIdx + idx;
        await addBookmarkToDB(currBookmark);
      });

      dispatch(setBookmarkDialogState(false));
      handleSnackbar("success", "Bookmark Successfully Added!", true);
      dispatch(setPreUploadedBookmarks([]));
      setTitle("");
      setGroup("");
      setDescription("");
      setError(false);
    } else {
      setError(true);
    }
  };

  function dialogClose() {
    dispatch(setBookmarkDialogState(false));
    dispatch(setPreUploadedBookmarks([]));
  }

  return (
    <Dialog
      open={bookmarkState.bookmarkDialogOpen}
      onClose={() => dialogClose()}
    >
      <DialogTitle>Bookmark</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Please provide additional information about the bookmark.
        </DialogContentText>
        <Autocomplete
          value={title}
          options={uniqueTitles}
          onChange={(event, newValue) => {
            setTitle(newValue as string);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Title"
              onChange={(e) => setTitle(e.target.value)}
            />
          )}
          freeSolo
          selectOnFocus
          handleHomeEndKeys
        />
        <TextField
          margin="dense"
          label="Description (Optional)"
          type="text"
          fullWidth
          onChange={(e) => setDescription(e.target.value)}
        />
        <Autocomplete
          value={group}
          options={uniqueGroups}
          onChange={(event, newValue) => {
            setGroup(newValue as string);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Group (New Group will be created if does not exist now)"
              onChange={(e) => setGroup(e.target.value)}
            />
          )}
          freeSolo
          selectOnFocus
          handleHomeEndKeys
        />
      </DialogContent>
      <DialogActions error={error}>
        {error && (
          <Typography color="error" sx={{ marginLeft: "18px" }}>
            Please fill in the Title!
          </Typography>
        )}
        <Button onClick={handleSubmit} color="primary">
          Add Bookmark
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookmarkDialog;
